import React, { useState, useId, forwardRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/**
 * Input Component
 * 
 * Production-grade text input with comprehensive features:
 * - Multiple input types (text, email, password, number, url, tel)
 * - Label (static or floating)
 * - Error states with messages
 * - Helper text
 * - Left/right icons
 * - Prefix/suffix text
 * - Character counter
 * - Password visibility toggle
 * - Size variants (sm, md, lg)
 * - Touch-friendly (44px height on mobile)
 * - Fully accessible (ARIA labels, error announcements)
 * - Dark mode compatible
 * - Mobile-first responsive
 * 
 * @example
 * ```tsx
 * <Input
 *   label="Email"
 *   type="email"
 *   value={email}
 *   onChange={setEmail}
 *   placeholder="Enter your email"
 *   error={errors.email}
 *   required
 * />
 * ```
 */

export interface InputProps {
  /** Input label text */
  label?: string;
  /** Label style variant */
  labelVariant?: 'static' | 'floating';
  /** Placeholder text */
  placeholder?: string;
  /** Input value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Input type */
  type?: 'text' | 'email' | 'password' | 'number' | 'url' | 'tel';
  /** Error message (shows error state) */
  error?: string;
  /** Helper text (shown below input) */
  helperText?: string;
  /** Icon to display on the left */
  leftIcon?: React.ReactNode;
  /** Icon to display on the right */
  rightIcon?: React.ReactNode;
  /** Prefix text (e.g., "$", "https://") */
  prefix?: string;
  /** Suffix text (e.g., "USD", ".com") */
  suffix?: string;
  /** Maximum character length */
  maxLength?: number;
  /** Show character counter */
  showCharCount?: boolean;
  /** Required field indicator */
  required?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Read-only state */
  readOnly?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Full width (100%) */
  fullWidth?: boolean;
  /** Auto-focus on mount */
  autoFocus?: boolean;
  /** Auto-complete attribute */
  autoComplete?: string;
  /** Name attribute */
  name?: string;
  /** ID attribute (auto-generated if not provided) */
  id?: string;
  /** Blur event handler */
  onBlur?: () => void;
  /** Focus event handler */
  onFocus?: () => void;
  /** Key down event handler */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Additional CSS classes */
  className?: string;
  /** ARIA label for accessibility */
  ariaLabel?: string;
  /** ARIA described by for accessibility */
  ariaDescribedBy?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      labelVariant = 'static',
      placeholder,
      value,
      onChange,
      type = 'text',
      error,
      helperText,
      leftIcon,
      rightIcon,
      prefix,
      suffix,
      maxLength,
      showCharCount = false,
      required = false,
      disabled = false,
      readOnly = false,
      size = 'md',
      fullWidth = false,
      autoFocus = false,
      autoComplete,
      name,
      id: providedId,
      onBlur,
      onFocus,
      onKeyDown,
      className = '',
      ariaLabel,
      ariaDescribedBy,
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const generatedId = useId();
    const id = providedId || generatedId;
    const errorId = `${id}-error`;
    const helperId = `${id}-helper`;
    const counterId = `${id}-counter`;

    // Handle password visibility toggle
    const isPasswordType = type === 'password';
    const actualType = isPasswordType && showPassword ? 'text' : type;

    // Determine if floating label should be raised
    const isFloatingRaised = labelVariant === 'floating' && (isFocused || value.length > 0);

    // Size classes
    const sizeClasses = {
      sm: 'text-sm h-9 px-3 min-h-[36px] sm:h-8 sm:min-h-[32px]', // 36px mobile, 32px desktop
      md: 'text-base h-12 px-4 min-h-[44px] sm:h-10 sm:min-h-[40px]', // 44px mobile, 40px desktop (WCAG compliant)
      lg: 'text-lg h-14 px-5 min-h-[48px] sm:h-12 sm:min-h-[48px]', // 48px mobile & desktop
    };

    // Base input classes
    const inputBaseClasses = `
      w-full
      rounded-lg
      border
      bg-white dark:bg-slate-800
      text-gray-900 dark:text-slate-100
      placeholder-gray-400 dark:placeholder-slate-500
      transition-all
      duration-200
      focus:outline-none
      focus:ring-2
      ${sizeClasses[size]}
      ${disabled ? 'cursor-not-allowed opacity-50 disabled:bg-gray-50 dark:disabled:bg-slate-800' : ''}
      ${readOnly ? 'cursor-default bg-gray-50 dark:bg-slate-900' : ''}
      ${error
        ? 'border-red-300 dark:border-red-400 focus:ring-red-500/20 focus:border-red-600'
        : 'border-gray-300 dark:border-slate-600 focus:ring-blue-500/20 focus:border-blue-500 dark:focus:border-blue-400'
      }
      ${leftIcon || prefix ? (size === 'sm' ? 'pl-8' : size === 'lg' ? 'pl-14' : 'pl-10') : ''}
      ${rightIcon || suffix || isPasswordType ? (size === 'sm' ? 'pr-8' : size === 'lg' ? 'pr-14' : 'pr-10') : ''}
    `.trim().replace(/\s+/g, ' ');

    // Container classes
    const containerClasses = `
      ${fullWidth ? 'w-full' : ''}
      ${className}
    `.trim();

    // Label classes
    const labelClasses = `
      block
      font-medium
      transition-all
      duration-200
      ${size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm'}
      ${error ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-slate-300'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      ${labelVariant === 'floating'
        ? isFloatingRaised
          ? 'absolute left-3 -top-2 text-xs bg-white dark:bg-slate-800 px-1'
          : 'absolute left-3 top-3 text-gray-400'
        : 'mb-1.5'
      }
    `.trim().replace(/\s+/g, ' ');

    // Icon container classes
    const iconContainerClasses = (position: 'left' | 'right') => `
      absolute
      ${position === 'left' ? 'left-3' : 'right-3'}
      top-1/2
      -translate-y-1/2
      ${size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5'}
      text-gray-400 dark:text-slate-500
      pointer-events-none
      flex-shrink-0
    `.trim().replace(/\s+/g, ' ');

    // Prefix/suffix classes
    const affixClasses = `
      absolute
      top-1/2
      -translate-y-1/2
      ${size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm'}
      text-gray-500 dark:text-slate-400
      pointer-events-none
    `.trim().replace(/\s+/g, ' ');

    // Handle change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Don't trigger onChange if disabled or readOnly
      if (disabled || readOnly) {
        return;
      }
      
      const newValue = e.target.value;
      
      // Enforce maxLength if specified
      if (maxLength && newValue.length > maxLength) {
        return;
      }
      
      onChange(newValue);
    };

    // Handle focus
    const handleFocus = () => {
      setIsFocused(true);
      onFocus?.();
    };

    // Handle blur
    const handleBlur = () => {
      setIsFocused(false);
      onBlur?.();
    };

    // Build aria-describedby
    const ariaDescriptions = [
      error ? errorId : null,
      helperText ? helperId : null,
      showCharCount ? counterId : null,
      ariaDescribedBy,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={containerClasses}>
        {/* Label */}
        {label && (
          <label htmlFor={id} className={labelClasses}>
            {label}
            {required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
          </label>
        )}

        {/* Input Container */}
        <div className={`relative ${labelVariant === 'floating' ? 'mt-2' : ''}`}>
          {/* Left Icon */}
          {leftIcon && (
            <div className={iconContainerClasses('left')}>
              {leftIcon}
            </div>
          )}

          {/* Prefix */}
          {prefix && (
            <span className={`${affixClasses} left-3`}>
              {prefix}
            </span>
          )}

          {/* Input */}
          <input
            ref={ref}
            id={id}
            name={name}
            type={actualType}
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            placeholder={labelVariant === 'floating' && !isFocused ? '' : placeholder}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            maxLength={maxLength}
            className={inputBaseClasses}
            aria-label={ariaLabel || label}
            aria-describedby={ariaDescriptions || undefined}
            aria-invalid={error ? 'true' : 'false'}
            aria-required={required}
          />

          {/* Suffix */}
          {suffix && (
            <span className={`${affixClasses} right-3`}>
              {suffix}
            </span>
          )}

          {/* Right Icon */}
          {rightIcon && !isPasswordType && (
            <div className={iconContainerClasses('right')}>
              {rightIcon}
            </div>
          )}

          {/* Password Toggle */}
          {isPasswordType && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={disabled}
              className={`
                absolute
                right-3
                top-1/2
                -translate-y-1/2
                ${size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5'}
                text-gray-400 dark:text-slate-500
                hover:text-gray-600 dark:hover:text-slate-300
                transition-colors
                focus:outline-none
                focus:ring-2
                focus:ring-blue-500/50
                rounded
                ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                flex items-center justify-center
                min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0
              `.trim().replace(/\s+/g, ' ')}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={0}
            >
              {showPassword ? (
                <EyeOff className={size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5'} />
              ) : (
                <Eye className={size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5'} />
              )}
            </button>
          )}
        </div>

        {/* Error Message / Helper Text / Character Counter Container */}
        {(error || helperText || showCharCount) && (
          <div className="flex items-start justify-between gap-2 mt-1.5">
            {/* Error or Helper Text */}
            <div className="flex-1 min-w-0">
              {error && (
                <p
                  id={errorId}
                  className={`text-sm text-red-600 dark:text-red-400 ${size === 'sm' ? 'text-xs' : ''}`}
                  role="alert"
                  aria-live="polite"
                >
                  {error}
                </p>
              )}
              {!error && helperText && (
                <p
                  id={helperId}
                  className={`text-sm text-gray-500 dark:text-slate-400 ${size === 'sm' ? 'text-xs' : ''}`}
                >
                  {helperText}
                </p>
              )}
            </div>

            {/* Character Counter */}
            {showCharCount && maxLength && (
              <span
                id={counterId}
                className={`
                  text-xs
                  flex-shrink-0
                  ${value.length >= maxLength
                    ? 'text-red-600 dark:text-red-400 font-medium'
                    : value.length >= maxLength * 0.9
                    ? 'text-yellow-600 dark:text-yellow-400 font-medium'
                    : 'text-gray-400 dark:text-slate-500'
                  }
                `}
                aria-live="polite"
                aria-atomic="true"
              >
                {value.length}/{maxLength}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

